<template>
  <div class="auto-enroll-panel">
    <div v-if="!placeId" class="auto-enroll-panel__hint">{{ text.needSaveFirst }}</div>

    <template v-else>
      <div class="auto-enroll-panel__bar">
        <span class="auto-enroll-panel__title">지금 배치가 돌면</span>
        <el-button size="mini" icon="el-icon-refresh" :loading="previewLoading" @click="reload">다시 읽기</el-button>
        <template v-if="!previewOnly && canManageLifecycle">
          <el-button size="mini" :disabled="busy" @click="run('DRY_RUN')">미리 돌려보기</el-button>
          <el-button size="mini" type="primary" :disabled="busy" @click="confirmApply">실제 편입</el-button>
        </template>
      </div>

      <!-- 실패는 「기록이 없다」와 다른 말이다 — 안내와 함께 다시 시도할 길을 준다(항목 A2·A5) -->
      <div v-if="previewFailed" class="auto-enroll-panel__hint error-text">
        {{ previewFailed }}
        <el-button size="mini" :loading="previewLoading" @click="loadPreview">{{ text.retry }}</el-button>
      </div>

      <div v-else-if="previewLoading" class="auto-enroll-panel__hint">읽는 중입니다...</div>

      <template v-else-if="preview">
        <div v-if="preview.placeSkip" class="auto-enroll-panel__skip">{{ placeSkipText }}</div>

        <template v-else>
          <div class="auto-enroll-panel__counts">
            <span
              >편입 예정 <b>{{ toAddRows.count }}</b
              >곳</span
            >
            <span v-if="preview.notInAreaCount">운영지역 밖 {{ preview.notInAreaCount }}곳</span>
            <!-- 거리 밖은 목록이 아니라 건수로 온다(서버가 `items` 에서 뺐다). 거리 조건이 꺼져 있으면 재지도 않았다 -->
            <span v-if="preview.distanceFilterOn && outOfRangeCount">기준 거리 밖 {{ outOfRangeCount }}곳</span>
            <span v-for="row in hiddenRows" :key="row.reason"
              >내 범위 밖이라 가려짐 · {{ row.label }} {{ row.count }}곳</span
            >
            <span v-if="preview.distanceFilterOn">거리 조건 {{ preview.maxDeliveryKm }}km</span>
          </div>

          <template v-if="toAddRows.count > 0">
            <el-table :data="toAddRows.items" size="mini" border max-height="260">
              <el-table-column label="매장" prop="storeNm" min-width="160" show-overflow-tooltip></el-table-column>
              <el-table-column label="매장 ID" prop="storeId" width="120"></el-table-column>
              <el-table-column label="주소" prop="storeAddr" min-width="200" show-overflow-tooltip></el-table-column>
              <el-table-column label="거리" width="90" align="right">
                <template slot-scope="scope">{{ distance(scope.row.distanceKm) }}</template>
              </el-table-column>
              <el-table-column label="지역 판정" width="110" align="center">
                <template slot-scope="scope">{{ regionBy(scope.row.regionBy) }}</template>
              </el-table-column>
            </el-table>
            <div v-if="toAddCapText" class="auto-enroll-panel__hint">{{ toAddCapText }}</div>
          </template>
          <div v-else class="auto-enroll-panel__hint">{{ text.previewEmpty }}</div>

          <el-collapse v-if="reasonGroups.length > 0" class="auto-enroll-panel__reasons">
            <el-collapse-item v-for="group in reasonGroups" :key="group.reason" :name="group.reason">
              <template slot="title">{{ group.label }} {{ group.count }}곳</template>
              <div v-for="item in group.items" :key="item.storeCd" class="auto-enroll-panel__reason-row">
                <span>{{ item.storeNm || item.storeId || item.storeCd }}</span>
                <span class="auto-enroll-panel__hint">{{ item.storeAddr }}</span>
                <span class="auto-enroll-panel__hint"
                  >{{ distance(item.distanceKm) }} · {{ regionBy(item.regionBy) }}</span
                >
              </div>
              <div v-if="capText(group)" class="auto-enroll-panel__hint">{{ capText(group) }}</div>
            </el-collapse-item>
          </el-collapse>
        </template>
      </template>

      <template v-if="!previewOnly">
        <div class="auto-enroll-panel__bar auto-enroll-panel__bar--runs">
          <span class="auto-enroll-panel__title">실행 기록</span>
        </div>
        <div v-if="runsFailed" class="auto-enroll-panel__hint error-text">
          {{ runsFailed }}
          <el-button size="mini" :loading="runsLoading" @click="loadRuns">{{ text.retry }}</el-button>
        </div>
        <el-table
          v-else
          v-loading="runsLoading"
          :data="runs"
          size="mini"
          border
          max-height="240"
          :empty-text="text.runsEmpty">
          <el-table-column label="시각" prop="startedAt" width="150"></el-table-column>
          <el-table-column label="모드" width="110" align="center">
            <template slot-scope="scope">{{ mode(scope.row.mode) }}</template>
          </el-table-column>
          <el-table-column label="계기" width="90" align="center">
            <template slot-scope="scope">{{ trigger(scope.row.triggerTp) }}</template>
          </el-table-column>
          <el-table-column label="후보" prop="candidateCount" width="70" align="right"></el-table-column>
          <el-table-column label="대상" prop="toAddCount" width="70" align="right"></el-table-column>
          <el-table-column label="편입" prop="addedCount" width="70" align="right"></el-table-column>
          <el-table-column label="건너뜀" min-width="200">
            <template slot-scope="scope">
              <span v-if="scope.row.placeSkip">{{ skipText(scope.row.placeSkip) }}</span>
              <span v-else>{{ skipped(scope.row.skippedJson) || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="오류" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.error || '-' }}</template>
          </el-table-column>
        </el-table>
      </template>
    </template>
  </div>
</template>

<script>
import { httpErrorMessage } from '@/plugins/http/policy'
import { latestGuard } from '@/utils/latestGuard'
import {
  PLACE_AREA_TEXT,
  RUN_MODE,
  distanceText,
  hiddenCountRows,
  outOfRangeCountOf,
  placeSkipNm,
  reasonGroupsOf,
  regionByText,
  rowCapText,
  runModeText,
  runTriggerText,
  skippedSummaryText,
  toAddRowsOf,
} from '@/utils/placeServiceArea'

/** 조회·실행이 세션을 끝내지 않는다 — 이 패널은 실패를 **페이지 안 안내**로 답한다(규약 §16.170·§16.175) */
const AUTO_ENROLL_CONFIG = { logoutOn403: false, showErrorMessage: false }

/**
 * 플레이스 하나의 **자동 편입 미리보기·실행 기록·수동 실행** (B7-a 항목 5·7).
 *
 * 문구는 전부 `utils/placeServiceArea` 가 만든다 — 여기에는 `사유 코드 → 한글` 매핑이 없다. 서버는
 * 어휘만 내리고(보고서 §5) 화면이 말을 만든다는 것이 이 축의 규약이다.
 *
 * `previewOnly` 는 **자동편입 토글의 확인 대화상자**가 쓰는 모양이다(항목 4). 같은 미리보기를 두 번
 * 그리지 않으려고 모양을 나눴을 뿐, 조회·판정은 한 곳이다.
 *
 * **「언제 다시 읽는가」의 답은 하나다**: 부모가 폼을 채울 때 올리는 [reloadSeq]. 종전에는 `placeId`
 * watcher 와 이 값이 **둘 다** 다시 읽기를 걸어 같은 preview 가 두 번 나갔고, 같은 플레이스를 연속으로
 * 열면 둘 다 안 변해 **저장 전 조건으로 계산된 미리보기**가 그대로 남았다. 신호가 둘이면 둘 중 하나를
 * 안 올린 자리가 반드시 생긴다.
 */
export default {
  name: 'PlaceAutoEnrollPanel',

  props: {
    placeId: { type: Number, default: null },
    /** 생명주기 축 — 실행 버튼은 이 역할에게만 보인다(서버도 같은 게이트로 403 을 낸다) */
    canManageLifecycle: { type: Boolean, default: false },
    /** 미리보기만 그린다(실행 기록·실행 버튼 없음) */
    previewOnly: { type: Boolean, default: false },
    /** 부모가 폼을 채울 때마다 올리는 세대 — 이 값이 바뀌면 다시 읽는다(유일한 신호) */
    reloadSeq: { type: Number, default: 0 },
    /**
     * 폼의 자동 편입 입력이 서버 값과 다른가 — 참이면 **실행을 열지 않는다**.
     *
     * 미리보기도 배치도 저장된 설정으로 도므로, 바뀐 폼 위에서 실행하면 사람이 본 목록과 실제 대상이
     * 갈린다. 판정은 부모가 순수 함수(`autoEnrollInputsChanged`)로 하고 이 패널은 그 답을 받는다.
     */
    unsavedSettings: { type: Boolean, default: false },
  },

  data() {
    return {
      text: PLACE_AREA_TEXT,
      preview: null,
      previewLoading: false,
      previewFailed: '',
      runs: [],
      runsLoading: false,
      runsFailed: '',
      busy: false,
      previewGuard: latestGuard(),
      runsGuard: latestGuard(),
    }
  },

  computed: {
    toAddRows() {
      return toAddRowsOf(this.preview && this.preview.items)
    },

    toAddCapText() {
      return rowCapText(this.toAddRows)
    },

    reasonGroups() {
      return reasonGroupsOf(this.preview && this.preview.items)
    },

    hiddenRows() {
      return hiddenCountRows(this.preview && this.preview.hiddenCounts)
    },

    outOfRangeCount() {
      return outOfRangeCountOf(this.preview)
    },

    placeSkipText() {
      return placeSkipNm(this.preview && this.preview.placeSkip)
    },
  },

  watch: {
    reloadSeq: 'reload',
  },

  created() {
    this.reload()
  },

  methods: {
    distance(km) {
      return distanceText(km)
    },

    regionBy(by) {
      return regionByText(by)
    },

    mode(value) {
      return runModeText(value)
    },

    trigger(value) {
      return runTriggerText(value)
    },

    skipText(code) {
      return placeSkipNm(code)
    },

    skipped(json) {
      return skippedSummaryText(json)
    },

    capText(view) {
      return rowCapText(view)
    },

    reload() {
      this.loadPreview()
      if (!this.previewOnly) this.loadRuns()
    },

    /**
     * 미리보기 — 시작할 때 **이전 대상의 결과를 비운다**. 안 비우면 B 의 다이얼로그에 A 의 매장 목록이
     * 잠깐 보인다(늦게 온 옛 응답은 `previewGuard` 가 막지만, 이미 그려진 옛 결과는 화면에 남는다).
     *
     * 성공·실패를 부모에게 알린다(항목 A2): 「켜기」는 **이 회차의 성공**이 있을 때만 열린다.
     */
    async loadPreview() {
      this.preview = null
      this.previewFailed = ''
      if (!this.placeId) {
        this.previewGuard.invalidate()
        this.previewLoading = false
        this.emitPreview(false)

        return
      }
      const token = this.previewGuard.begin()
      this.previewLoading = true
      this.emitPreview(false)
      try {
        const response = await this.$axios.get(`/place/${this.placeId}/auto-enroll/preview`, AUTO_ENROLL_CONFIG)
        if (!this.previewGuard.isLatest(token)) return
        this.preview = response.data || null
        this.previewFailed = ''
        // 대상 0건인 정상 응답도 성공이다 — 「읽었다」와 「들어갈 매장이 있다」는 다른 질문이다
        this.emitPreview(true)
      } catch (error) {
        if (!this.previewGuard.isLatest(token)) return
        this.preview = null
        this.previewFailed = httpErrorMessage(error, this.text.previewLoadFailed)
        this.emitPreview(false)
      } finally {
        if (this.previewGuard.isLatest(token)) this.previewLoading = false
      }
    },

    /** 이 패널이 **지금 보고 있는 대상**의 조회 결과 — 부모는 대상까지 보고 판정한다 */
    emitPreview(ok) {
      this.$emit('preview', { placeId: this.placeId, ok: ok === true })
    },

    /** 실행 기록 — **못 읽은 것과 기록이 없는 것은 다른 답**이다(항목 A5). 빈 기록 문구는 성공에만 */
    async loadRuns() {
      this.runs = []
      this.runsFailed = ''
      if (!this.placeId) {
        this.runsGuard.invalidate()
        this.runsLoading = false

        return
      }
      const token = this.runsGuard.begin()
      this.runsLoading = true
      try {
        const response = await this.$axios.get(
          `/place/${this.placeId}/auto-enroll/runs`,
          Object.assign({ params: { limit: 20 } }, AUTO_ENROLL_CONFIG)
        )
        if (!this.runsGuard.isLatest(token)) return
        this.runs = (response.data && response.data.items) || []
        this.runsFailed = ''
      } catch (error) {
        if (!this.runsGuard.isLatest(token)) return
        this.runs = []
        this.runsFailed = httpErrorMessage(error, this.text.runsLoadFailed)
      } finally {
        if (this.runsGuard.isLatest(token)) this.runsLoading = false
      }
    },

    confirmApply() {
      if (this.blockedByUnsaved()) return

      this.$confirm(this.text.runApplyConfirm, '자동 편입 실행', {
        confirmButtonText: '편입',
        cancelButtonText: '취소',
        type: 'warning',
      })
        .then(() => this.run(RUN_MODE.APPLY))
        .catch(() => {})
    },

    /** 바뀐 설정 위에서 실행하면 사람이 본 미리보기와 대상이 갈린다 — 저장을 먼저 하게 한다(항목 A1) */
    blockedByUnsaved() {
      if (!this.unsavedSettings) return false
      this.$message({ type: 'warning', message: this.text.unsavedBeforeRun })

      return true
    },

    /** 실행은 이 플레이스 하나만 지목한다 — `placeIds` 를 비우면 서버가 볼 수 있는 전부를 돈다 */
    async run(mode) {
      if (!this.placeId || this.busy) return
      if (this.blockedByUnsaved()) return
      this.busy = true
      try {
        const response = await this.$axios.post(
          '/place/auto-enroll/run',
          { mode, placeIds: [this.placeId] },
          AUTO_ENROLL_CONFIG
        )
        const result = ((response.data && response.data.places) || [])[0] || {}
        this.$message({
          type: 'success',
          message:
            mode === RUN_MODE.APPLY
              ? `편입 ${result.added || 0}곳 (대상 ${result.toAdd || 0}곳)`
              : `미리 돌려봤습니다 — 대상 ${result.toAdd || 0}곳`,
        })
        if (mode === RUN_MODE.APPLY) this.$emit('applied')
      } catch (error) {
        // 서버가 준 문장을 그대로 보인다 — 409 「자동 편입이 이미 실행 중입니다.」·403 「…권한이 없습니다.」
        // 같은 뜻의 문구를 화면이 따로 만들면 서버가 문장을 고칠 때 둘이 갈린다(서버 보고서 ⑦)
        this.$message({ type: 'error', message: httpErrorMessage(error, this.text.runFailed) })
      } finally {
        this.busy = false
        this.reload()
      }
    },
  },
}
</script>

<style scoped>
.auto-enroll-panel__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.auto-enroll-panel__bar--runs {
  margin-top: 16px;
}

.auto-enroll-panel__title {
  font-weight: 600;
}

.auto-enroll-panel__hint {
  color: #909399;
  font-size: 12px;
  padding: 6px 0;
}

.auto-enroll-panel__skip {
  padding: 10px;
  background: #fdf6ec;
  color: #e6a23c;
  border-radius: 4px;
}

.auto-enroll-panel__counts {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
  font-size: 13px;
}

.auto-enroll-panel__reasons {
  margin-top: 10px;
}

.auto-enroll-panel__reason-row {
  display: flex;
  gap: 12px;
  padding: 2px 0;
  font-size: 12px;
}

.error-text {
  color: #f56c6c;
}
</style>
